import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";

const STR = {
  no: {
    heading: "Meldinger til admin",
    intro:
      "Skriv direkte til oss om noe oppstår underveis. Vi ser meldingen når vi logger inn. Trenger du raskt svar, send en WhatsApp til manager Joakim Maylove på +47 900 21 105.",
    placeholder: "Skriv en melding …",
    send: "Send",
    empty: "Ingen meldinger ennå. Skriv den første under.",
  },
  en: {
    heading: "Messages to host",
    intro: "Write to us if anything comes up during your stay. We see your message when we log in. For a fast response, send a WhatsApp to manager Joakim Maylove on +47 900 21 105.",
    placeholder: "Write a message …",
    send: "Send",
    empty: "No messages yet. Write the first one below.",
  },
};

/**
 * Inline messaging widget inside the customer portal – kunde ↔ admin only.
 * Polls every 30s; instantly refreshes after sending.
 */
export default function CustomerMessages({ lang = "no" }) {
  const { user } = useAuth();
  const s = STR[lang] || STR.no;
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const threadRef = useRef(null);

  useEffect(() => {
    const fetchMsgs = () => {
      api.get("/messages")
        .then((r) => setMessages(r.data))
        .catch(() => {});
    };
    fetchMsgs();
    api.post("/messages/mark-read", {}).catch(() => {});
    const t = setInterval(fetchMsgs, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    // Scroll only the thread container itself (never the window) so the
    // customer portal doesn't auto-scroll to the bottom on first paint.
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!draft.trim()) return;
    setSending(true);
    try {
      await api.post("/messages", { body: draft.trim() });
      setDraft("");
      const r = await api.get("/messages");
      setMessages(r.data);
    } catch {
      toast.error(lang === "en" ? "Could not send" : "Kunne ikke sende");
    } finally {
      setSending(false);
    }
  };

  const me = user?.id;

  return (
    <Card className="p-6 sm:p-8" data-testid="customer-messages">
      <div className="flex items-center gap-2">
        <MessageSquare className="w-5 h-5 text-[#BD984D]" />
        <h2 className="text-xl font-semibold text-[#0C1824]">{s.heading}</h2>
      </div>
      <p className="text-sm text-[#2B3F4D] mt-1">{s.intro}</p>

      <div ref={threadRef} className="mt-5 space-y-2 max-h-[280px] overflow-y-auto pr-1" data-testid="customer-msg-thread">
        {messages.length === 0 && (
          <p className="text-sm text-[#2B3F4D] py-4 text-center">{s.empty}</p>
        )}
        {messages.map((m) => {
          const mine = m.from_user_id === me;
          return (
            <div
              key={m.id}
              data-testid={`customer-msg-${m.id}`}
              className={`flex ${mine ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-3.5 py-2 ${
                  mine
                    ? "bg-[#0C1824] text-white rounded-br-sm"
                    : "bg-[#BD984D]/15 text-[#0C1824] rounded-bl-sm"
                }`}
              >
                <div className={`text-[10px] uppercase tracking-wider mb-0.5 ${
                  mine ? "text-white/70" : "text-[#A57F39]"
                }`}>
                  {m.from_name} · {m.from_role}
                </div>
                <div className="whitespace-pre-wrap text-sm">{m.body}</div>
                <div className={`text-[10px] mt-1 ${mine ? "text-white/60" : "text-[#2B3F4D]"}`}>
                  {new Date(m.created_at).toLocaleString(lang === "en" ? "en-GB" : "nb-NO", {
                    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex gap-2">
        <Textarea
          rows={2}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={s.placeholder}
          data-testid="customer-msg-input"
          className="resize-none"
        />
        <Button
          onClick={send}
          disabled={sending || !draft.trim()}
          data-testid="customer-msg-send"
          className="bg-[#1DA3B3] hover:bg-[#2FB5C5] text-white self-end h-11 px-4"
        >
          <Send className="w-4 h-4 sm:mr-1.5" />
          <span className="hidden sm:inline">{s.send}</span>
        </Button>
      </div>
    </Card>
  );
}
